/* Enhance existing release nodes. No release wording, dates, links or order are rewritten. */
(() => {
  if (customElements.get('mm-release-history')) return;
  let instance = 0;
  const normalized = (value) => value.replace(/\s+/g, ' ').trim();
  const versionPattern = /^v?(\d+\.\d+(?:\.\d+)?)\b[\s\S]*?\brelease\b/i;

  class MuteMeReleaseHistory extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => this.initialize());
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.removeListeners?.forEach((remove) => remove());
      this.removeListeners = [];
      this.initialized = false;
    }

    initialize() {
      if (this.initialized || !this.isConnected) return;
      if (!this.querySelector('[data-release-end]')) {
        this.frame = requestAnimationFrame(() => this.initialize());
        return;
      }
      this.content = this.querySelector('[data-release-content]');
      this.search = this.querySelector('[data-release-search]');
      this.links = this.querySelector('[data-release-links]');
      if (!this.content || !this.search || !this.links) return;

      if (!this.hasAttribute('data-release-ready') && !this.prepareEntries()) return;
      this.entries = Array.from(this.content.querySelectorAll('[data-release-entry]')).map((element) => ({
        element,
        heading: element.querySelector('[data-release-heading]'),
        text: normalized(element.textContent).toLocaleLowerCase(),
      }));
      if (!this.entries.length) return;
      this.buildIndex();
      this.removeListeners = [];
      const listen = (target, type, handler) => {
        target.addEventListener(type, handler);
        this.removeListeners.push(() => target.removeEventListener(type, handler));
      };
      listen(this.search, 'input', () => this.filter());
      listen(this.search, 'search', () => this.filter());
      listen(this.querySelector('[data-release-clear]'), 'click', () => {
        this.search.value = '';
        this.filter();
        this.search.focus();
      });
      listen(this.links, 'click', (event) => {
        const link = event.target.closest('a[data-release-target]');
        if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const entry = this.entries.find((item) => item.heading.id === link.dataset.releaseTarget);
        if (entry) entry.heading.focus({ preventScroll: true });
      });
      listen(window, 'hashchange', () => this.revealHash());
      this.querySelector('[data-release-tools]').hidden = false;
      this.querySelector('[data-release-index]').hidden = false;
      this.initialized = true;
      this.filter();
      this.revealHash();
    }

    prepareEntries() {
      const headings = Array.from(this.content.querySelectorAll('h2,h3,h4,p')).filter((element) => versionPattern.test(normalized(element.textContent)));
      // Preserve an unfamiliar merchant structure verbatim instead of guessing at section boundaries.
      if (headings.length < 2 || headings.some((heading) => heading.parentElement !== headings[0].parentElement)) return false;
      const container = headings[0].parentElement;
      const headingSet = new Set(headings);
      const nodes = Array.from(container.childNodes);
      const fragment = document.createDocumentFragment();
      const intro = document.createElement('div');
      intro.className = 'mm-release-notes__intro';
      fragment.append(intro);
      let destination = intro;
      const prefix = `mm-release-${++instance}`;
      const occurrences = new Map();
      nodes.forEach((node) => {
        if (headingSet.has(node)) {
          const version = normalized(node.textContent).match(versionPattern)[1];
          const count = (occurrences.get(version) || 0) + 1;
          occurrences.set(version, count);
          if (!node.id) {
            const stem = `${prefix}-${version.replace(/\./g, '-')}-${count}`;
            let id = stem;
            let suffix = 0;
            while (document.getElementById(id)) id = `${stem}-${++suffix}`;
            node.id = id;
          }
          node.setAttribute('data-release-heading', '');
          node.setAttribute('role', 'heading');
          node.setAttribute('aria-level', '2');
          node.tabIndex = -1;
          destination = document.createElement('article');
          destination.className = 'mm-release-notes__entry';
          destination.setAttribute('data-release-entry', '');
          destination.setAttribute('aria-labelledby', node.id);
          fragment.append(destination);
        }
        destination.append(node);
      });
      container.append(fragment);
      this.content.querySelectorAll('p,div,ul,ol').forEach((node) => {
        if (!normalized(node.textContent) && !node.querySelector('a,img,video,iframe,svg,input,button')) node.setAttribute('data-release-spacer', '');
      });
      this.setAttribute('data-release-ready', '');
      return true;
    }

    buildIndex() {
      this.links.replaceChildren();
      this.entries.forEach((entry) => {
        const text = normalized(entry.heading.textContent);
        const version = text.match(versionPattern)[1];
        const date = text.match(/\b\d{1,2}[-/]\d{1,2}[-/]\d{4}\b/);
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${entry.heading.id}`;
        link.dataset.releaseTarget = entry.heading.id;
        link.append(document.createTextNode(version));
        if (date) {
          const stamp = document.createElement('span');
          stamp.textContent = date[0];
          link.append(stamp);
        }
        item.append(link);
        this.links.append(item);
        entry.indexItem = item;
      });
    }

    filter() {
      const query = normalized(this.search.value).toLocaleLowerCase();
      const terms = query.split(' ').filter(Boolean);
      let visible = 0;
      this.entries.forEach((entry) => {
        const matches = terms.every((term) => entry.text.includes(term));
        entry.element.hidden = !matches;
        entry.indexItem.hidden = !matches;
        if (matches) visible++;
      });
      this.querySelector('[data-release-clear]').hidden = !this.search.value;
      this.querySelector('[data-release-empty]').hidden = visible !== 0;
      this.querySelector('[data-release-count]').textContent = query
        ? `${visible} of ${this.entries.length} release entries`
        : `${this.entries.length} release entries`;
    }

    revealHash() {
      let id;
      try { id = decodeURIComponent(window.location.hash.slice(1)); } catch { return; }
      const target = id && document.getElementById(id);
      if (!target || !this.content.contains(target)) return;
      const entry = target.closest('[data-release-entry]');
      if (entry?.hidden) {
        this.search.value = '';
        this.filter();
      }
      target.scrollIntoView({ block: 'start' });
    }
  }

  customElements.define('mm-release-history', MuteMeReleaseHistory);
})();
